import React, { useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiStar, FiExternalLink } from 'react-icons/fi';

const QuotesView = ({ quotes, document, documentName, onViewInDocument }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  if (!quotes || quotes.length === 0) {
    return (
      <div style={{
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'var(--foreground-muted)',
        fontSize: '14px'
      }}>
        No quotes available
      </div>
    );
  }
  
  const currentQuote = quotes[currentIndex];
  
  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : quotes.length - 1));
  };
  
  const goToNext = () => {
    setCurrentIndex((prev) => (prev < quotes.length - 1 ? prev + 1 : 0));
  };
  
  return (
    <div style={{ 
      height: '100%', 
      overflowY: 'auto',
      background: 'linear-gradient(to bottom, var(--bg-primary) 0%, #0d0d10 100%)'
    }}>
      {/* Navigation Header */}
      <div style={{
        padding: '20px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        background: 'var(--bg-secondary)',
        position: 'sticky',
        top: 0,
        zIndex: 10
      }}>
        <button
          onClick={goToPrevious}
          style={{
            background: 'var(--bg-tertiary)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--foreground)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'var(--bg-tertiary)';
            e.target.style.transform = 'scale(1)';
          }}
        >
          <FiChevronLeft size={18} />
        </button>
        
        <div style={{
          fontSize: '14px',
          fontWeight: '600',
          color: 'var(--foreground)',
          letterSpacing: '0.02em'
        }}>
          Quote {currentIndex + 1} of {quotes.length}
        </div>
        
        <button
          onClick={goToNext}
          style={{
            background: 'var(--bg-tertiary)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            width: '36px',
            height: '36px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--foreground)',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'var(--bg-tertiary)';
            e.target.style.transform = 'scale(1)';
          }}
        >
          <FiChevronRight size={18} />
        </button>
      </div>
      
      {/* Quote Content */}
      <div style={{
        maxWidth: '850px',
        margin: '0 auto',
        padding: '48px 36px',
      }}>
        <div style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.03) 0%, rgba(118, 75, 162, 0.03) 100%)',
          border: '1px solid rgba(102, 126, 234, 0.15)',
          borderRadius: '16px',
          padding: '40px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.03)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Decorative gradient overlay */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
            opacity: 0.6
          }} />
          
          {/* Header with category and page */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px', 
            marginBottom: '28px',
            flexWrap: 'wrap',
            marginTop: '6px'
          }}>
            {currentQuote.category && (
              <span style={{
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '13px',
                fontWeight: '600',
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                color: '#a1a3ff',
                border: '1px solid rgba(102, 126, 234, 0.3)',
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.1)',
                letterSpacing: '0.02em'
              }}>
                {currentQuote.category}
              </span>
            )}
            {currentQuote.significance && (
              <span style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '13px', 
                color: '#fbbf24',
                fontWeight: '600'
              }}>
                {(() => {
                  const starCount = (currentQuote.significance?.match(/⭐/g) || []).length;
                  if (starCount > 0) {
                    return Array(starCount).fill(0).map((_, i) => (
                      <FiStar key={i} size={14} fill="#fbbf24" style={{ filter: 'drop-shadow(0 0 4px rgba(251, 191, 36, 0.4))' }} />
                    ));
                  }
                  return currentQuote.significance;
                })()}
              </span>
            )}
            {currentQuote.page_reference && (
              <span style={{ 
                marginLeft: 'auto', 
                fontSize: '13px', 
                color: 'var(--foreground-muted)',
                fontWeight: '500',
                letterSpacing: '0.01em'
              }}>
                Page {currentQuote.page_reference}
              </span>
            )}
          </div>
          
          {/* Quote Text */}
          <blockquote style={{
            margin: '0 0 28px 0',
            padding: '0 0 0 24px',
            borderLeft: '4px solid rgba(102, 126, 234, 0.4)',
            fontSize: '18px',
            lineHeight: '1.8',
            color: 'var(--foreground)',
            fontStyle: 'italic',
            letterSpacing: '0.01em'
          }}>
            "{currentQuote.quote}"
          </blockquote>
          
          {/* View in Document Button */}
          {(document || onViewInDocument) && (
            <button
              onClick={() => {
                if (onViewInDocument) {
                  onViewInDocument(currentQuote);
                } else {
                  // Dispatch event to open document with highlighted quote
                  window.dispatchEvent(new CustomEvent('viewQuoteInDocument', {
                    detail: {
                      quote: currentQuote,
                      document: document,
                      documentName: documentName
                    }
                  }));
                }
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 20px',
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)',
                border: '1px solid rgba(102, 126, 234, 0.3)',
                borderRadius: '10px',
                color: '#a1a3ff',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '24px'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.25) 0%, rgba(118, 75, 162, 0.25) 100%)';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <FiExternalLink size={16} />
              View in Document
            </button>
          )}
          
          {/* Analysis - handle both string and object formats */}
          {currentQuote.analysis && (
            <div style={{
              background: 'rgba(102, 126, 234, 0.04)',
              borderRadius: '12px',
              padding: '24px',
              border: '1px solid rgba(102, 126, 234, 0.12)',
              marginTop: '24px'
            }}>
              <div style={{
                fontSize: '13px',
                fontWeight: '700',
                color: '#a1a3ff',
                marginBottom: '16px',
                textTransform: 'uppercase',
                letterSpacing: '0.08em'
              }}>
                Analysis
              </div>
              {typeof currentQuote.analysis === 'string' ? (
                <div style={{
                  fontSize: '15px',
                  lineHeight: '1.8',
                  color: 'var(--foreground)',
                  letterSpacing: '0.01em'
                }}>
                  {currentQuote.analysis}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {currentQuote.analysis.key_idea && (
                    <div style={{
                      padding: '16px',
                      background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(102, 126, 234, 0.04) 100%)',
                      borderRadius: '10px',
                      border: '1px solid rgba(102, 126, 234, 0.2)'
                    }}>
                      <div style={{ fontSize: '11px', color: '#667eea', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        💡 Key Idea
                      </div>
                      <div style={{ fontSize: '14px', lineHeight: '1.7', color: 'var(--foreground)' }}>
                        {currentQuote.analysis.key_idea}
                      </div>
                    </div>
                  )}
                  {currentQuote.analysis.why_important && (
                    <div style={{
                      padding: '16px',
                      background: 'linear-gradient(135deg, rgba(52, 211, 153, 0.08) 0%, rgba(52, 211, 153, 0.04) 100%)',
                      borderRadius: '10px',
                      border: '1px solid rgba(52, 211, 153, 0.2)'
                    }}>
                      <div style={{ fontSize: '11px', color: '#34d399', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        ⭐ Why Important
                      </div>
                      <div style={{ fontSize: '14px', lineHeight: '1.7', color: 'var(--foreground)' }}>
                        {currentQuote.analysis.why_important}
                      </div>
                    </div>
                  )}
                  {currentQuote.analysis.practical_use && (
                    <div style={{
                      padding: '16px',
                      background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.08) 0%, rgba(251, 191, 36, 0.04) 100%)',
                      borderRadius: '10px',
                      border: '1px solid rgba(251, 191, 36, 0.2)'
                    }}>
                      <div style={{ fontSize: '11px', color: '#fbbf24', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        🔧 Practical Use
                      </div>
                      <div style={{ fontSize: '14px', lineHeight: '1.7', color: 'var(--foreground)' }}>
                        {currentQuote.analysis.practical_use}
                      </div>
                    </div>
                  )}
                  {currentQuote.analysis.connections && (
                    <div style={{
                      padding: '16px',
                      background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.08) 0%, rgba(168, 85, 247, 0.04) 100%)',
                      borderRadius: '10px',
                      border: '1px solid rgba(168, 85, 247, 0.2)'
                    }}>
                      <div style={{ fontSize: '11px', color: '#a855f7', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        🔗 Connections
                      </div>
                      <div style={{ fontSize: '14px', lineHeight: '1.7', color: 'var(--foreground)' }}>
                        {currentQuote.analysis.connections}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          {/* Context - handle both string and object formats */}
          {currentQuote.context && (
            <div style={{
              marginTop: '24px',
              padding: '20px',
              background: 'rgba(0, 0, 0, 0.2)',
              borderRadius: '10px',
              border: '1px solid rgba(255, 255, 255, 0.06)'
            }}>
              <div style={{
                fontSize: '12px',
                fontWeight: '600',
                color: 'var(--foreground-muted)',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.06em'
              }}>
                Context
              </div>
              {typeof currentQuote.context === 'string' ? (
                <div style={{
                  fontSize: '14px',
                  lineHeight: '1.7',
                  color: 'var(--foreground-muted)',
                  letterSpacing: '0.01em'
                }}>
                  {currentQuote.context}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {currentQuote.context.position_in_argument && (
                    <div>
                      <span style={{ fontSize: '12px', color: '#667eea', fontWeight: '600' }}>Position: </span>
                      <span style={{ fontSize: '14px', color: 'var(--foreground-muted)' }}>{currentQuote.context.position_in_argument}</span>
                    </div>
                  )}
                  {currentQuote.context.what_precedes && (
                    <div>
                      <span style={{ fontSize: '12px', color: '#667eea', fontWeight: '600' }}>Before: </span>
                      <span style={{ fontSize: '14px', color: 'var(--foreground-muted)' }}>{currentQuote.context.what_precedes}</span>
                    </div>
                  )}
                  {currentQuote.context.what_follows && (
                    <div>
                      <span style={{ fontSize: '12px', color: '#667eea', fontWeight: '600' }}>After: </span>
                      <span style={{ fontSize: '14px', color: 'var(--foreground-muted)' }}>{currentQuote.context.what_follows}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuotesView;
